/**
 * Error Boundary Component
 * Catches React errors and displays user-friendly fallback UI
 * Provides graceful degradation when WebGL or other features fail
 * Nuamedia Solutions Pty Ltd
 */
import React, { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorType: 'webgl' | 'network' | 'animation' | 'unknown';
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorType: 'unknown',
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Determine error type based on error message
    let errorType: ErrorBoundaryState['errorType'] = 'unknown';

    // Safely get error message - error or error.message might be undefined
    const errorMessage = (error?.message || String(error) || '').toLowerCase();
    if (errorMessage.includes('webgl') || errorMessage.includes('context')) {
      errorType = 'webgl';
    } else if (errorMessage.includes('network') || errorMessage.includes('fetch') || errorMessage.includes('load') || errorMessage.includes('model')) {
      errorType = 'network';
    } else if (errorMessage.includes('animation') || errorMessage.includes('clip')) {
      errorType = 'animation';
    }

    return {
      hasError: true,
      error: error || new Error('Unknown error occurred'),
      errorType,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);

    this.setState({
      errorInfo,
    });

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log to error reporting service (if configured)
    this.logErrorToService(error, errorInfo);
  }

  private logErrorToService(error: Error, errorInfo: ErrorInfo): void {
    // TODO: Integrate with error reporting service (e.g., Sentry, LogRocket)
    console.log('Error logged:', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
    });
  }

  private retry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorType: 'unknown',
    });
  };

  private renderFallback(): ReactNode {
    const { error, errorType, errorInfo } = this.state;
    const { showDetails = false } = this.props;

    // Custom fallback if provided
    if ('fallback' in this.props) {
      return this.props.fallback;
    }

    // Default fallback UI based on error type
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          {errorType === 'webgl' && this.renderWebGLFallback()}
          {errorType === 'network' && this.renderNetworkFallback()}
          {errorType === 'animation' && this.renderAnimationFallback()}
          {errorType === 'unknown' && this.renderGenericFallback()}

          <button onClick={this.retry} style={styles.retryButton}>
            Try Again
          </button>

          {showDetails && error && (
            <details style={styles.details}>
              <summary>Error Details</summary>
              <pre style={styles.errorText}>
                {error.message}
                {'\n\n'}
                {error.stack}
                {errorInfo && '\n\nComponent Stack:'}
                {errorInfo?.componentStack}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }

  private renderWebGLFallback(): ReactNode {
    return (
      <div style={styles.errorContent}>
        <h2 style={styles.title}>WebGL Not Supported</h2>
        <p style={styles.message}>
          Your browser doesn't support WebGL, which is required for 3D avatar rendering.
        </p>
        <div style={styles.suggestions}>
          <p><strong>Suggestions:</strong></p>
          <ul style={styles.list}>
            <li>Update your browser to the latest version</li>
            <li>Enable hardware acceleration in browser settings</li>
            <li>Try a different browser (Chrome, Firefox, Edge, Safari)</li>
          </ul>
        </div>
        {/* Fallback: Show 2D avatar image */}
        <div style={styles.fallbackAvatar}>
          <div style={styles.avatar2D}>👤</div>
          <p style={styles.fallbackText}>2D Mode Active</p>
        </div>
      </div>
    );
  }

  private renderNetworkFallback(): ReactNode {
    return (
      <div style={styles.errorContent}>
        <h2 style={styles.title}>Connection Error</h2>
        <p style={styles.message}>
          Failed to load avatar resources. Please check your internet connection.
        </p>
        <div style={styles.suggestions}>
          <p><strong>Troubleshooting:</strong></p>
          <ul style={styles.list}>
            <li>Check your internet connection</li>
            <li>Disable VPN or proxy if enabled</li>
            <li>Try refreshing the page</li>
          </ul>
        </div>
      </div>
    );
  }

  private renderAnimationFallback(): ReactNode {
    return (
      <div style={styles.errorContent}>
        <h2 style={styles.title}>Animation Error</h2>
        <p style={styles.message}>
          There was an issue loading avatar animations.
        </p>
        <p style={styles.submessage}>
          The avatar may still function with limited animations.
        </p>
      </div>
    );
  }

  private renderGenericFallback(): ReactNode {
    return (
      <div style={styles.errorContent}>
        <h2 style={styles.title}>Something Went Wrong</h2>
        <p style={styles.message}>
          The avatar encountered an unexpected error.
        </p>
        <p style={styles.submessage}>
          Please try refreshing the page or contact support if the problem persists.
        </p>
      </div>
    );
  }

  render() {
    if (this.state.hasError) {
      return this.renderFallback();
    }

    return this.props.children;
  }
}

// Styles
const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    width: '100vw',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  } as React.CSSProperties,

  content: {
    maxWidth: '600px',
    padding: '40px',
    textAlign: 'center' as const,
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    borderRadius: '20px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  } as React.CSSProperties,

  errorContent: {
    padding: '20px',
  } as React.CSSProperties,

  title: {
    fontSize: '2em',
    marginBottom: '20px',
    fontWeight: 600,
  } as React.CSSProperties,

  message: {
    fontSize: '1.1em',
    marginBottom: '15px',
    lineHeight: 1.5,
  } as React.CSSProperties,

  submessage: {
    fontSize: '0.95em',
    opacity: 0.8,
    marginTop: '10px',
  } as React.CSSProperties,

  suggestions: {
    marginTop: '30px',
    textAlign: 'left' as const,
    background: 'rgba(255, 255, 255, 0.1)',
    padding: '20px',
    borderRadius: '10px',
  } as React.CSSProperties,

  list: {
    marginTop: '10px',
    paddingLeft: '20px',
  } as React.CSSProperties,

  retryButton: {
    marginTop: '30px',
    padding: '12px 30px',
    fontSize: '1em',
    background: '#fff',
    color: '#667eea',
    border: 'none',
    borderRadius: '25px',
    cursor: 'pointer',
    fontWeight: 600,
    transition: 'transform 0.2s, box-shadow 0.2s',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
  } as React.CSSProperties,

  details: {
    marginTop: '30px',
    textAlign: 'left' as const,
    background: 'rgba(0, 0, 0, 0.2)',
    padding: '15px',
    borderRadius: '8px',
    fontSize: '0.85em',
  } as React.CSSProperties,

  errorText: {
    whiteSpace: 'pre-wrap' as const,
    overflow: 'auto',
    maxHeight: '200px',
    fontSize: '0.85em',
    lineHeight: 1.4,
  } as React.CSSProperties,

  fallbackAvatar: {
    marginTop: '30px',
  } as React.CSSProperties,

  avatar2D: {
    fontSize: '100px',
    margin: '20px auto',
  } as React.CSSProperties,

  fallbackText: {
    fontSize: '0.9em',
    opacity: 0.7,
  } as React.CSSProperties,
};

export default ErrorBoundary;

